import React, {useState} from "react";
import {
    Button,
    Divider,
    Grid,
    IconButton, List,
    ListItemText, Menu,
    MenuItem,
    Popover,
    Select, Stack,
    Typography
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import SortIcon from "@mui/icons-material/Sort";
import ClearIcon from "@mui/icons-material/Clear";
import {EntityTableHeaderProps} from "../EntityList";
import FilterRenderer from "./filterInputs/FilterRenderer";

interface TableFiltersProps {
    isMobile: boolean;
    headers: EntityTableHeaderProps[];
    filters: Record<string, string>;
    setFilter: (key: string, value: string) => void;
    removeFilter: (key: string) => void;
    sortKeys: string[];
    sortDirections: ('asc' | 'desc')[];
    setSort: (key: string | null, direction: 'asc' | 'desc' | null) => void;
}

const TableFilters: React.FC<TableFiltersProps> = ({
                                                       isMobile,
                                                       headers,
                                                       filters,
                                                       setFilter,
                                                       removeFilter,
                                                       sortKeys,
                                                       sortDirections,
                                                       setSort,
                                                   }) => {
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
    const [addSortAnchorEl, setAddSortAnchorEl] = useState<null | HTMLElement>(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

    const handleSortMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setSortAnchorEl(null);
    };

    const handleFilterMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterMenuClose = () => {
        setFilterAnchorEl(null);
    };

    const handleAddSortMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAddSortAnchorEl(event.currentTarget);
    }

    const handleAddSortMenuClose = () => {
        setAddSortAnchorEl(null);
    }

    return (
        isMobile ? (
                <>
                    {headers.some((header) => header.filtered) && (
                        <Button onClick={handleFilterMenuClick}
                                color={Object.keys(filters).length > 0 ? "primary" : "inherit"}>
                            <FilterListIcon/> Filtrování
                        </Button>
                    )}
                    {headers.some((header) => header.sortable) && (
                        <Button onClick={handleSortMenuClick}
                                color={sortKeys.length > 0 && sortKeys.some((key) => key !== "id") ? "primary" : "inherit"}>
                            <SortIcon/> Řazení
                        </Button>
                    )}
                    <Popover
                        anchorEl={filterAnchorEl}
                        open={Boolean(filterAnchorEl)}
                        onClose={handleFilterMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{p: 2}}>Filtry</Typography>
                        {headers
                            .filter((header) => header.filtered)
                            .map((header) => (
                                <MenuItem key={header.key}>
                                    <FilterRenderer
                                        config={{
                                            ...header.filterConfig,
                                            label: header.label,
                                            type: header.filterConfig?.type ?? 'text'
                                        }}
                                        value={filters[header.key] || ''}
                                        setFilter={(value) => setFilter(header.key, value)}
                                        removeFilter={() => removeFilter(header.key)}
                                        isMobile={isMobile}
                                    />
                                </MenuItem>
                            ))}
                        <Divider/>
                        {Object.keys(filters).length > 0 && (
                            <MenuItem>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        Object.keys(filters).forEach((key) => removeFilter(key));
                                    }}
                                >
                                    Zrušit filtry
                                </Button>
                            </MenuItem>
                        )}
                    </Popover>
                    <Popover
                        anchorEl={sortAnchorEl}
                        open={Boolean(sortAnchorEl)}
                        onClose={handleSortMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{p: 2}}>Řazení</Typography>

                        <List>
                            {sortKeys.map((key, index) => (
                                key === "id"
                                    ? (
                                        <MenuItem key={key}>
                                            <Typography>Výchozí řazení</Typography>
                                        </MenuItem>
                                    ) : (
                                        <MenuItem key={key}>
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item xs>
                                                    <Typography noWrap>
                                                        {headers.find((header) => header.key === key)?.label}:
                                                    </Typography>
                                                </Grid>

                                                <Grid item>
                                                    <Select
                                                        value={sortDirections[index]}
                                                        onChange={(e) => setSort(key, e.target.value as 'asc' | 'desc')}
                                                        variant="standard"
                                                        fullWidth
                                                        sx={{minWidth: 100}}
                                                    >
                                                        <MenuItem value="asc">Vzestupně</MenuItem>
                                                        <MenuItem value="desc">Sestupně</MenuItem>
                                                    </Select>
                                                </Grid>

                                                <Grid item>
                                                    <IconButton
                                                        onClick={() => setSort(key, null)}
                                                        size="small"
                                                    >
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    )
                            ))}
                        </List>


                        {sortKeys.length < headers.filter((header) => header.sortable).length && (
                            <MenuItem>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon/>}
                                    fullWidth
                                    onClick={handleAddSortMenuClick}
                                >
                                    Přidat řazení
                                </Button>
                            </MenuItem>
                        )}

                        <Menu
                            anchorEl={addSortAnchorEl}
                            open={Boolean(addSortAnchorEl)}
                            onClose={handleAddSortMenuClose}
                        >
                            {headers
                                .filter((header) => header.sortable && !sortKeys.includes(header.key))
                                .map((header) => (
                                    <MenuItem
                                        key={header.key}
                                        onClick={() => {
                                            setSort(header.key, 'asc');
                                            handleAddSortMenuClose();
                                        }}
                                    >
                                        <ListItemText primary={header.label}/>
                                    </MenuItem>
                                ))}
                        </Menu>

                        {sortKeys.length > 0 && sortKeys.some((key) => key !== "id") && (
                            <MenuItem>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        setSort(null, null);
                                    }}
                                    startIcon={<ClearIcon/>}
                                    fullWidth
                                >
                                    Zrušit všechna řazení
                                </Button>
                            </MenuItem>
                        )}
                    </Popover>
                </>
            ) :
            (
                <Stack direction="row" gap={2} alignItems="center"
                       sx={{overflow: 'auto', mb: headers.filter((header) => header.filtered).length > 0 ? 0 : 3}}>
                    {headers.some((header) => header.filtered) && (
                        <Typography sx={{pt: 3}}>Filtry:</Typography>
                    )}
                    {headers
                        .filter((header) => header.filtered)
                        .map((header) => (
                                <FilterRenderer
                                    key={header.key}
                                    config={{
                                        ...header.filterConfig,
                                        label: header.label,
                                        type: header.filterConfig?.type ?? 'text'
                                    }}
                                    value={filters[header.key] || ''}
                                    setFilter={(value) => setFilter(header.key, value)}
                                    removeFilter={() => removeFilter(header.key)}
                                    isMobile={isMobile}
                                />
                            )
                        )}
                    {Object.keys(filters).length > 0 && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                Object.keys(filters).forEach((key) => removeFilter(key));
                            }}
                            size="small"
                        >
                            Zrušit filtry
                        </Button>
                    )}
                </Stack>
            )
    );
};

export default TableFilters;